/**
 * Shared query language for search (ADR-0022, E2). A query is split into
 * discrete `field:value` filters and free text. Both the frontend local search
 * and the server `/api/search` parse with this single grammar so the `field:`
 * DSL behaves identically everywhere.
 *
 * Supported prefixes: `uuid:`/`id:`, `name:`, `kind:`, `owner:`, `tag:`,
 * `barcode:`. In addition, a bare token that matches the UUID shape is
 * auto-routed to the id filter — a cheap, unambiguous convenience (a UUID
 * cannot be meaningful free text for semantic search).
 *
 * Date keys (`bestBefore`, `returnBy`, `expiresOn`, `warrantyUntil`,
 * `purchasedOn`) take a validated day-precision date and a comparison operator:
 * `:` or `=` for an exact day, `<=`/`>=` for inclusive bounds, `<`/`>` for
 * strict ones (ADR-0037). A date that does not validate falls back to free
 * text, exactly like an unknown prefix.
 *
 * Most fields are container-level; `id`, `barcode` and every date key also
 * match items (filterItemMatches). Items carry no name/kind/owner/tag fields to
 * filter on, and containers only carry the `expiresOn` date.
 */
import { CONTAINER_DEADLINE_KEYS, ITEM_DATE_KEYS, dateProperty, isIsoDate, } from "./properties.js";
import { foldText } from "./search-scoring.js";
/**
 * The queryable date vocabulary — the union of the item and container
 * property keys, so `properties.ts` stays the single source of truth.
 */
export const DATE_FIELD_KEYS = [
    ...new Set([...ITEM_DATE_KEYS, ...CONTAINER_DEADLINE_KEYS]),
];
const DATE_FIELD_SET = new Set(DATE_FIELD_KEYS);
/** Operator syntax a date token accepts; `:` is normalised to `=`. */
export const DATE_OPERATORS = [":", "=", "<", "<=", ">", ">="];
const DATE_OPERATOR_SET = new Set(DATE_OPERATORS);
/** True for a key that takes a date value and comparison operator. */
export function isDateField(field) {
    return DATE_FIELD_SET.has(field);
}
/** True for a date key containers can carry (`expiresOn` only, ADR-0037). */
function isContainerDateField(field) {
    return CONTAINER_DEADLINE_KEYS.includes(field);
}
/** True when a parsed query carries no free text (pure-discrete lookup). */
export function isPureDiscrete(parsed) {
    return parsed.freeText.trim().length === 0;
}
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const PREFIX_FIELDS = {
    uuid: "id",
    id: "id",
    name: "name",
    kind: "kind",
    owner: "owner",
    tag: "tag",
    barcode: "barcode",
};
/**
 * One token as `field` + operator + value. The leading group must start with a
 * letter so a bare UUID never looks like a field token.
 */
const FIELD_TOKEN_RE = /^([A-Za-z][A-Za-z0-9]*)(<=|>=|<|>|=|:)(.*)$/;
/**
 * Parse one whitespace-delimited token into a discrete filter, or undefined
 * when it should stay free text. Date keys require a valid `YYYY-MM-DD` value;
 * an invalid date is free text rather than a filter that silently matches
 * nothing (deadlines 05).
 */
function filterForToken(token) {
    const match = FIELD_TOKEN_RE.exec(token);
    if (!match)
        return undefined;
    const prefix = match[1] ?? "";
    const operator = match[2] ?? "";
    const value = match[3] ?? "";
    if (isDateField(prefix))
        return dateFilterFor(prefix, operator, value);
    // Non-date fields keep the original colon-only grammar (`=` is only a date
    // comparison, so `kind=box` stays free text).
    if (PREFIX_FIELDS[prefix] && operator === ":" && value) {
        return { field: PREFIX_FIELDS[prefix], value };
    }
    return undefined;
}
function dateFilterFor(field, operator, value) {
    if (!DATE_OPERATOR_SET.has(operator) || !isIsoDate(value))
        return undefined;
    return { field, value, op: operator === ":" ? "=" : operator };
}
/**
 * Split a search query into discrete filters and residual free text.
 *
 * Each `field:value` token is a single discrete value (values with spaces are a
 * future enhancement via quoting). Date keys additionally accept `=`, `<`,
 * `<=`, `>` and `>=` before a validated `YYYY-MM-DD` value. A bare UUID token
 * becomes an id filter. All other whitespace-delimited tokens are joined back
 * into the free-text portion.
 */
export function parseQuery(query) {
    const trimmed = query.trim();
    if (!trimmed)
        return { filters: [], freeText: "" };
    const tokens = trimmed.split(/\s+/);
    const filters = [];
    const free = [];
    for (const token of tokens) {
        if (!token)
            continue;
        const filter = filterForToken(token);
        if (filter) {
            filters.push(filter);
            continue;
        }
        // Bare UUID → discrete id filter (ADR-0022 convenience).
        if (UUID_RE.test(token)) {
            filters.push({ field: "id", value: token });
            continue;
        }
        // Unknown prefix (e.g. "https://"), a malformed date or an empty value —
        // treat as free text.
        free.push(token);
    }
    return { filters, freeText: free.join(" ") };
}
/**
 * Compare an entity's date property against a validated date filter. Both
 * sides are `YYYY-MM-DD`, so a lexical compare is calendar order; `<=`/`>=`
 * are inclusive, `<`/`>` strict. Unset, malformed or non-date properties never
 * match.
 */
function matchesDateFilter(filter, properties) {
    if (!isIsoDate(filter.value))
        return false;
    const date = dateProperty(properties, filter.field);
    if (!date || !isIsoDate(date))
        return false;
    switch (filter.op ?? "=") {
        case "=":
            return date === filter.value;
        case "<":
            return date < filter.value;
        case "<=":
            return date <= filter.value;
        case ">":
            return date > filter.value;
        case ">=":
            return date >= filter.value;
        default:
            return false;
    }
}
/**
 * True when a filter can select an item: the item identity fields and every
 * date key. An item-only query (e.g. a pure `bestBefore<=…`) must surface item
 * hits, while container-only fields never do (deadlines 05).
 */
export function filterAppliesToItems(filter) {
    return filter.field === "id" || filter.field === "barcode" || isDateField(filter.field);
}
/**
 * Apply one discrete filter to a container (ADR-0022, C1). The semantics are
 * shared verbatim by the frontend local search and the server `/api/search` so
 * a `field:` value matches the same containers everywhere.
 */
export function filterMatchesFilter(filter, c) {
    switch (filter.field) {
        case "id":
            return c.id === filter.value;
        case "name":
            return c.name === filter.value || c.name.toLowerCase().includes(filter.value.toLowerCase());
        case "kind":
            return c.kind.toLowerCase() === filter.value.toLowerCase();
        case "owner":
            return (c.owner ?? "").toLowerCase() === filter.value.toLowerCase();
        case "tag":
            return (c.tags ?? []).some((t) => t.toLowerCase() === filter.value.toLowerCase());
        case "barcode":
            return false;
        default:
            // Only `expiresOn` is a container date; the other date keys are item-only.
            return isContainerDateField(filter.field) && matchesDateFilter(filter, c.properties);
    }
}
/** Keep non-deleted containers passing every discrete filter (ADR-0022, C1-C2). */
export function filterContainers(containers, filters) {
    return containers.filter((c) => !c.deleted && filters.every((f) => filterMatchesFilter(f, c)));
}
/**
 * Apply one discrete filter to an item. `id`, `barcode` and every date key are
 * item-level fields; barcode compares folded (hyphens/whitespace-insensitive,
 * ADR-0004) so `barcode:978-3-…` and `barcode:9783…` agree. A date key compares
 * the item's typed property (ADR-0037).
 */
export function filterItemMatches(filter, item) {
    switch (filter.field) {
        case "id":
            return item.id === filter.value;
        case "barcode":
            return foldText(item.barcode) === foldText(filter.value);
        case "name":
        case "kind":
        case "owner":
        case "tag":
            return false;
        default:
            return matchesDateFilter(filter, item.properties);
    }
}
/** Keep items passing every discrete filter (identity fields and date keys). */
export function filterItems(items, filters) {
    return items.filter((item) => filters.every((f) => filterItemMatches(f, item)));
}
//# sourceMappingURL=query.js.map